import * as Sentry from '@sentry/react';
import type { DeviceInfo, AudioSource } from '../stores';

const DIAG = '[AUDIO-BUG-12009:DeviceConfig]';
const SHADOWCAM_DIAG = '[BLUE-STREAM-12010:DeviceConfig]';

/**
 * Chromium serves enumerateDevices() from a cached device list that only refreshes on OS
 * device-change notifications. Dynamically-registered virtual DirectShow cameras (ShadowCams)
 * may NOT emit a WM_DEVICECHANGE, so the cache can go stale and a just-plugged ShadowCam can be
 * missing from a subsequent enumeration (root cause candidate for BLUE-STREAM-12010 "capture
 * returned null"). These helpers give us high-signal visibility into exactly what Chromium
 * returned on each enumeration so we can tell "device never enumerated" from "resolver rejected".
 */
const summarizeShadowcam = (sc: DeviceInfo): string =>
  `${sc.displayName}#${sc.deviceId ? sc.deviceId.slice(0, 8) : 'no-id'}`;

/**
 * DeviceConfigManager - Handles device enumeration, selection, mute/volume controls.
 *
 * Responsibilities:
 * - Enumerate available video/audio devices
 * - Manage device selection state
 * - Mute/unmute controls
 * - Volume management
 */
export type EnumeratedDeviceCounts = {
  audioinput: number;
  videoinput: number;
  audiooutput: number;
};

export class DeviceConfigManager {
  private cameras: DeviceInfo[] = [];
  private shadowcams: DeviceInfo[] = [];
  private videoInputs: MediaDeviceInfo[] = [];
  private microphones: AudioSource[] = [];
  private desktopAudioSources: AudioSource[] = [];
  private selectedCameraId: string | null = null;
  private selectedMicrophoneId: string | null = null;
  private selectedDesktopAudioSource: string | null = null;
  private videoMuted = false;
  private audioMuted = false;
  private volume = 1.0;
  private lastDeviceCounts: EnumeratedDeviceCounts = { audioinput: 0, videoinput: 0, audiooutput: 0 };
  private onDeviceListChanged: (() => void) | null = null;
  private deviceChangeHandler: (() => void) | null = null;
  // Snapshot of the ShadowCam labels from the previous enumeration, so each enumeration can log
  // the delta (added/removed slots) — the key signal for BLUE-STREAM-12010 stale-cache diagnosis.
  private lastShadowcamLabels: string[] = [];
  private enumerateCount = 0;

  public setOnDeviceListChanged(callback: () => void): void {
    this.onDeviceListChanged = callback;
  }

  /**
   * Subscribe to Chromium's `devicechange` event and re-enumerate when it fires.
   *
   * Critical for BLUE-STREAM-12010: dynamic pool plugs enumerate asynchronously after
   * host-electron toggles a hub route. Without this listener, boot-time enumeration
   * misses newly plugged slots and capture fails until the next manual refresh.
   *
   * Idempotent: safe to call repeatedly; only the first call subscribes.
   */
  public startDeviceChangeListener(): void {
    if (this.deviceChangeHandler) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.addEventListener) return;
    this.deviceChangeHandler = () => {
      console.log(`${DIAG} devicechange event — re-enumerating`);
      Sentry.logger.info(`${SHADOWCAM_DIAG} devicechange fired — re-enumerating devices`);
      Sentry.addBreadcrumb({
        category: 'BLUE-STREAM-12010',
        message: 'navigator.mediaDevices devicechange fired',
        level: 'info',
        data: { previousShadowcamCount: this.lastShadowcamLabels.length },
      });
      void this.enumerateDevices();
    };
    Sentry.logger.info(`${SHADOWCAM_DIAG} devicechange listener subscribed`);
    navigator.mediaDevices.addEventListener('devicechange', this.deviceChangeHandler);
  }

  private stopDeviceChangeListener(): void {
    if (!this.deviceChangeHandler) return;
    if (typeof navigator !== 'undefined' && navigator.mediaDevices?.removeEventListener) {
      navigator.mediaDevices.removeEventListener('devicechange', this.deviceChangeHandler);
    }
    this.deviceChangeHandler = null;
  }

  public async enumerateDevices(): Promise<void> {
    const enumId = ++this.enumerateCount;
    const startedAt = Date.now();
    Sentry.logger.info(`${SHADOWCAM_DIAG} enumerateDevices() call #${enumId} started`);
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const elapsedMs = Date.now() - startedAt;

      const audioInputCount = devices.filter((d) => d.kind === 'audioinput').length;
      const videoInputCount = devices.filter((d) => d.kind === 'videoinput').length;
      const audioOutputCount = devices.filter((d) => d.kind === 'audiooutput').length;
      const emptyLabelCount = devices.filter((d) => !d.label).length;
      this.lastDeviceCounts = {
        audioinput: audioInputCount,
        videoinput: videoInputCount,
        audiooutput: audioOutputCount,
      };
      console.log(`${DIAG} Enumerated devices: ${audioInputCount} audioinput, ${videoInputCount} videoinput, ${audioOutputCount} audiooutput, ${emptyLabelCount} empty labels`);

      if (emptyLabelCount > 0) {
        console.warn(`${DIAG} ${emptyLabelCount} device(s) have empty labels — possible permissions issue`);
      }

      this.videoInputs = devices.filter((device) => device.kind === 'videoinput');
      this.cameras = this.parseVideoDevices(devices);
      this.shadowcams = this.parseShadowcams(devices);
      this.microphones = this.parseMicrophones(devices);
      this.buildDesktopAudioSources(devices);

      // --- BLUE-STREAM-12010 diagnostics: exactly which ShadowCams Chromium surfaced this pass ---
      // parseShadowcams filters videoinput devices whose label CONTAINS "ShadowCam"; if a
      // just-plugged slot is missing here it will never be resolvable for capture, so log the
      // full set plus the delta vs the previous enumeration and any empty-label video devices
      // (empty label => permission gap => the "ShadowCam" substring match silently fails).
      const shadowcamLabels = this.shadowcams.map((sc) => sc.displayName);
      const addedShadowcams = shadowcamLabels.filter((l) => !this.lastShadowcamLabels.includes(l));
      const removedShadowcams = this.lastShadowcamLabels.filter((l) => !shadowcamLabels.includes(l));
      const emptyLabelVideoCount = devices.filter((d) => d.kind === 'videoinput' && !d.label).length;

      console.log(
        `${SHADOWCAM_DIAG} enum #${enumId} shadowcams=[${this.shadowcams.map(summarizeShadowcam).join(', ')}] ` +
        `added=[${addedShadowcams.join(', ')}] removed=[${removedShadowcams.join(', ')}] ` +
        `emptyLabelVideo=${emptyLabelVideoCount} elapsedMs=${elapsedMs}`,
      );

      Sentry.logger.info(
        `${SHADOWCAM_DIAG} enumerateDevices #${enumId} complete: ${this.shadowcams.length} shadowcam(s) ` +
        `[${shadowcamLabels.join(', ')}] (video=${videoInputCount}, emptyLabelVideo=${emptyLabelVideoCount}, elapsedMs=${elapsedMs})`,
      );

      Sentry.addBreadcrumb({
        category: 'BLUE-STREAM-12010',
        message: `enumerateDevices #${enumId}: ${this.shadowcams.length} shadowcam(s)`,
        level: emptyLabelVideoCount > 0 ? 'warning' : 'info',
        data: {
          enumId,
          shadowcamCount: this.shadowcams.length,
          shadowcamLabels,
          addedShadowcams,
          removedShadowcams,
          videoInputCount,
          emptyLabelCount,
          emptyLabelVideoCount,
          elapsedMs,
        },
      });

      // Video devices with empty labels means Chromium withheld labels (no active media permission
      // grant) — our ShadowCam matching is label-based, so this is a hard blocker worth an alert.
      if (emptyLabelVideoCount > 0) {
        Sentry.captureMessage(
          `${SHADOWCAM_DIAG} ${emptyLabelVideoCount} video device(s) enumerated with empty labels — ShadowCam label matching will miss them`,
          {
            level: 'warning',
            tags: { shadowcam_diag: 'BLUE-STREAM-12010', shadowcam_stage: 'enumerate_devices' },
            extra: { enumId, videoInputCount, emptyLabelVideoCount },
          },
        );
      }

      if (addedShadowcams.length > 0 || removedShadowcams.length > 0) {
        Sentry.logger.info(
          `${SHADOWCAM_DIAG} shadowcam set changed on enum #${enumId}: +[${addedShadowcams.join(', ')}] -[${removedShadowcams.join(', ')}]`,
        );
      }
      this.lastShadowcamLabels = shadowcamLabels;

      this.onDeviceListChanged?.();
    } catch (error) {
      console.error(`${DIAG} Failed to enumerate devices:`, error);
      Sentry.captureException(error, {
        tags: {
          audio_stage: 'enumerate_devices',
          audio_diag: 'AUDIO-BUG-12009',
          shadowcam_diag: 'BLUE-STREAM-12010',
          shadowcam_stage: 'enumerate_devices',
        },
        extra: { enumId },
      });
    }
  }

  public getCameras(): DeviceInfo[] {
    return this.cameras;
  }

  public getShadowcams(): DeviceInfo[] {
    return this.shadowcams;
  }

  /**
   * Read advertised FPS from the last enumerated video devices, using the positive
   * scalar or finite, positive range maximum. Returns undefined for absent devices
   * or unusable capabilities; errors from getCapabilities propagate.
   */
  public frameRateForDevice(deviceId: string): number | undefined {
    const device = this.videoInputs.find((entry) => entry.deviceId === deviceId) as
      | (MediaDeviceInfo & { getCapabilities?: () => MediaTrackCapabilities })
      | undefined;
    const range = device?.getCapabilities?.().frameRate;
    if (typeof range === 'number') return Number.isFinite(range) && range > 0 ? range : undefined;
    const max = range && typeof range === 'object' ? range.max : undefined;
    return typeof max === 'number' && Number.isFinite(max) && max > 0 ? max : undefined;
  }

  public getEnumeratedDeviceCounts(): EnumeratedDeviceCounts {
    return { ...this.lastDeviceCounts };
  }

  public getMicrophones(): AudioSource[] {
    return this.microphones;
  }

  public getDesktopAudioSources(): AudioSource[] {
    return this.desktopAudioSources;
  }

  public selectCamera(deviceId: string): void {
    this.selectedCameraId = deviceId;
    localStorage.setItem('selectedCameraId', deviceId);
  }

  public selectMicrophone(deviceId: string): void {
    this.selectedMicrophoneId = deviceId;
    localStorage.setItem('selectedMicId', deviceId);
    this.microphones = this.microphones.map((mic) => ({
      ...mic,
      selected: mic.deviceId === deviceId,
    }));
  }

  public selectDesktopAudioSource(source: string): void {
    this.selectedDesktopAudioSource = source;
    localStorage.setItem('selectedDesktopAudioSource', source);
    this.desktopAudioSources = this.desktopAudioSources.map((s) => ({
      ...s,
      selected: s.deviceId === source,
    }));
  }

  public getSelectedCameraId(): string | null {
    return this.selectedCameraId;
  }

  public getSelectedMicrophoneId(): string | null {
    return this.selectedMicrophoneId;
  }

  public getSelectedDesktopAudioSource(): string | null {
    return this.selectedDesktopAudioSource;
  }

  public setVideoMuted(muted: boolean): void {
    this.videoMuted = muted;
  }

  public setAudioMuted(muted: boolean): void {
    this.audioMuted = muted;
  }

  public isVideoMuted(): boolean {
    return this.videoMuted;
  }

  public isAudioMuted(): boolean {
    return this.audioMuted;
  }

  public setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  public getVolume(): number {
    return this.volume;
  }

  public destroy(): void {
    this.stopDeviceChangeListener();
    this.onDeviceListChanged = null;
  }

  private parseVideoDevices(devices: MediaDeviceInfo[]): DeviceInfo[] {
    const cameras = devices.filter((d) => d.kind === 'videoinput' && !d.label.includes('ShadowCam'));
    const regex = / \([0-9a-fA-F]+:[0-9a-fA-F]+\)$/;
    return cameras.map((camera) => ({
      deviceId: camera.deviceId,
      displayName: camera.label.replace(regex, '').trim(),
    }));
  }

  private parseShadowcams(devices: MediaDeviceInfo[]): DeviceInfo[] {
    return devices
      .filter((d) => d.kind === 'videoinput' && d.label.includes('ShadowCam'))
      .map((d) => ({ deviceId: d.deviceId, displayName: d.label }));
  }

  private parseMicrophones(devices: MediaDeviceInfo[]): AudioSource[] {
    const mics = devices.filter(
      (d) => d.kind === 'audioinput' && !d.label.startsWith('Default') && !d.label.startsWith('Communications'),
    );
    const regex = / \([0-9a-fA-F]+:[0-9a-fA-F]+\)$/;
    const parsed = mics.map((mic) => ({
      deviceId: mic.deviceId,
      label: mic.label.replace(regex, '').trim(),
    }));
    const deduped = parsed.filter((mic, idx, self) => self.findIndex((m) => m.label === mic.label) === idx);
    const storedMicId = localStorage.getItem('selectedMicId');

    const sources: AudioSource[] = [
      { deviceId: '0', label: 'None', selected: !storedMicId || deduped.every((m) => m.deviceId !== storedMicId) },
      ...deduped.map((mic) => ({
        deviceId: mic.deviceId,
        label: mic.label,
        selected: storedMicId === mic.deviceId,
      })),
    ];

    return sources;
  }

  private buildDesktopAudioSources(devices: MediaDeviceInfo[]): void {
    const stored = localStorage.getItem('selectedDesktopAudioSource');
    this.desktopAudioSources = [
      { deviceId: '0', label: 'None', selected: stored === '0' },
      { deviceId: 'desktop', label: 'Desktop Audio', selected: stored === 'desktop' || !stored },
    ];

    const gameCaptureDevice = devices.find((d) => d.label.includes('AudioCaster Virtual Device'));
    if (gameCaptureDevice) {
      console.log(`${DIAG} AudioCaster present in device list: "${gameCaptureDevice.label}"`);
      this.desktopAudioSources.push({
        deviceId: 'gameCapture',
        label: 'Game Capture',
        selected: stored === 'gameCapture',
      });
    } else {
      console.warn(`${DIAG} AudioCaster NOT present in desktop audio source list`);
    }
  }
}
